import { GrElement, TOKENS_CSS } from "../core/base";
import { audio, type AudioOutputDevice } from "../core/audio";
import { browserCapabilities } from "../core/capabilities";

/**
 * <gr-audio-output></gr-audio-output>
 * <gr-audio-output label="Wall speakers"></gr-audio-output>
 *
 * The audio routing pill — the speaker analog of the display picker in
 * <gr-output>. Shows the shared AudioContext's state (muted / live) and,
 * where AudioContext.setSinkId() exists (Chromium), a picker that routes
 * the page's audio to a specific output device, persisted per page.
 *
 * Placing the element creates the shared context (granrojo.audio), so the
 * first interaction anywhere unlocks it; clicking the pill also works.
 * Debuggability is the point: a silent installation should show
 * "muted" or the wrong sink at a glance.
 */
export class GrAudioOutput extends GrElement {
  connectedCallback(): void {
    const supportsSink = browserCapabilities().sinkSelection;
    const root = this.attachShadow({ mode: "open" });
    root.innerHTML = `
      <style>
        ${TOKENS_CSS}
        :host { display: inline-flex; }
        .pill {
          display: inline-flex;
          align-items: center;
          border: 1px solid var(--_border);
          background: var(--_bg);
          border-radius: 999px;
        }
        button {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          border: none;
          background: transparent;
          color: var(--_text);
          font-family: var(--_font);
          font-size: 11px;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          padding: 9px 12px 9px 16px;
          cursor: pointer;
        }
        .dot {
          width: 8px;
          height: 8px;
          border-radius: 999px;
          background: var(--_muted);
          transition: 0.2s;
        }
        :host([data-state="running"]) .dot { background: #34d399; box-shadow: 0 0 8px rgba(52, 211, 153, 0.8); }
        :host([data-state="suspended"]) .dot { background: #fbbf24; }
        .detail { color: var(--_muted); font-size: 10px; letter-spacing: normal; text-transform: none; }
        select {
          border: none;
          border-left: 1px solid var(--_border);
          background: transparent;
          color: var(--_muted);
          font-family: var(--_mono);
          font-size: 10px;
          padding: 9px 14px 9px 10px;
          outline: none;
          cursor: pointer;
          max-width: 160px;
        }
        select:hover, select:focus { color: var(--_accent); }
      </style>
      <span class="pill">
        <button type="button" class="main" part="button">
          <span class="dot" part="dot"></span>
          <span class="text"></span>
          <span class="detail"></span>
        </button>
        ${supportsSink ? '<select class="sink" title="Audio output device"></select>' : ""}
      </span>
    `;

    root.querySelector(".text")!.textContent = this.attr("label", "audio");

    root.querySelector(".main")?.addEventListener("click", () => {
      void audio.context().resume().catch(() => {});
    });

    const select = root.querySelector<HTMLSelectElement>(".sink");
    select?.addEventListener("change", () => {
      void audio.setSink(select.value).catch((err: unknown) => {
        this.setDetail(err instanceof Error ? err.message : String(err));
      });
    });

    // Own the shared context so unlock arms immediately.
    audio.context();
    this.track(
      audio.subscribe((status) => {
        this.setAttribute("data-state", status.state);
        this.setDetail(status.state === "running" ? "" : status.state === "suspended" ? "muted" : status.state);
        if (select && select.value !== status.sinkId) select.value = status.sinkId;
      }),
    );

    if (select) {
      void this.populate(select);
      navigator.mediaDevices?.addEventListener("devicechange", this.onDeviceChange);
    }
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    navigator.mediaDevices?.removeEventListener("devicechange", this.onDeviceChange);
  }

  private onDeviceChange = (): void => {
    const select = this.shadowRoot?.querySelector<HTMLSelectElement>(".sink");
    if (select) void this.populate(select);
  };

  private async populate(select: HTMLSelectElement): Promise<void> {
    let outputs: AudioOutputDevice[] = [];
    try {
      outputs = await audio.outputs();
    } catch {
      // No device enumeration — leave just the default option.
    }
    const current = audio.status().sinkId;
    select.innerHTML = "";
    const fallback = document.createElement("option");
    fallback.value = "";
    fallback.textContent = "System default";
    select.appendChild(fallback);
    for (const output of outputs) {
      if (output.deviceId === "" || output.deviceId === "default") continue;
      const option = document.createElement("option");
      option.value = output.deviceId;
      option.textContent = output.label;
      select.appendChild(option);
    }
    select.value = Array.from(select.options).some((o) => o.value === current) ? current : "";
  }

  private setDetail(text: string): void {
    const detail = this.shadowRoot?.querySelector(".detail");
    if (detail) detail.textContent = text;
  }
}

customElements.define("gr-audio-output", GrAudioOutput);
